<template>
	<a
		class="country-item"
		tabindex="0"
		:href="addEnabledParams(item.URL)"
		:aria-label="`Details zu ${item.Name} anzeigen`"
		:class="{ 'is-summary': isSummary !== false }"
	>
		<div class="country-item__name">
			{{ item.Name }}
		</div>
		<div class="country-item__container">
			<span class="country-item__details">
				<span
					v-if="item.HotelCount"
					class="country-item__hotel"
					:aria-label="`${item.HotelCount} Hotels verfügbar`"
				>
					{{ item.HotelCount }} Hotels
				</span>
				<div class="country-item__temperature-info">
					<span
						v-if="item.AirTemperature && item.AirTemperature !== '-'"
						class="country-item__air"
						:aria-label="`Lufttemperatur beträgt ${item.AirTemperature} Grad`"
					>
						{{ temperature(item.AirTemperature) }}
					</span>
					<span
						v-if="item.WaterTemperature && item.WaterTemperature !== '-'"
						class="country-item__water"
						:aria-label="`Wassertemperatur beträgt ${item.WaterTemperature} Grad`"
					>
						{{ temperature(item.WaterTemperature) }}
					</span>
				</div>
				<span
					v-if="!isHotelOnly"
					:class="['country-item__flight', { 'country-item__flight--hidden': !item.FlightDuration }]"
					:aria-label="`Flugdauer beträgt ${formatFlightDuration(item.FlightDuration)}`"
				>
					{{ formatFlightDuration(item.FlightDuration) }}
				</span>
			</span>
			<span
				v-if="(item.AirTemperature && item.AirTemperature !== '-') || (item.FlightDuration && !isHotelOnly)"
				class="country-item__user-info"
				:aria-label="`Reisedauer: ${getDaysDurationLabel()}, für ${getTravelersCount()}`"
			>
				{{ getDaysDurationLabel() }}
				<span>,&nbsp;{{ getTravelersCount() }}</span>
			</span>
		</div>
		<div class="country-item__price">
			<span
				:href="addEnabledParams(item.URL)"
				class="country-item__button"
				:aria-label="`Preis ab ${formatPrice(getTotalPrice(item), currency)}`"
			>
				<span class="country-item__button-prefix">
					<span>{{ TOTAL_PRICE_TEXT }}</span>
				</span>
				<BasePrice :price="getTotalPrice(item)" />
			</span>
		</div>
	</a>
</template>

<script lang="ts" setup>
import { addEnabledParams } from '@services/url';
import { pluralize } from '@utils/utils';
import { formatPrice } from '@/js/utils/priceUtils';
import { formatFlightDuration } from '@/js/utils/dateTimeUtils'; // Import the utility function
import { type PackageRegion } from '@/interfaces/api/v1-best-offer-for-region';
import { useStore } from '@/components/common/store';
import { TOTAL_PRICE_TEXT } from '@global-js/constants';
import BasePrice from '@lmt-rpb/Atoms/BasePrice/BasePrice.vue';
import type { SearchFormDataType } from '@interfaces/search-form';
import { computed } from 'vue';

const store = useStore();

interface Props {
	item: Partial<PackageRegion>;
	currency: string;
	isSummary?: boolean;
}

withDefaults(defineProps<Props>(), {
	currency: 'EUR',
	isSummary: false,
});

const formData = computed((): SearchFormDataType => store.state.searchMask);
const isHotelOnly = computed((): boolean => formData.value.onlyHotel);

const getDaysDurationLabel = (duration = store.state.searchMask.travelDuration) => {
	let result: string;
	if (duration?.length === 1) {
		result = pluralize(Number(duration[0]), 'Tag', 'Tage');
	} else {
		result = `${duration?.join('-')} Tage`;
	}
	return result;
};

const getTravelersCount = () => {
	const travelers = store.state.searchMask.travelers.adult;
	const children = store.state.searchMask.travelers.children.length;
	return travelers + children + ' Pers.';
};

const temperature = (value: string) => (value !== '-' && `${value}°`) || value;

const getTotalPrice = (item: Partial<PackageRegion>) => {
	const price = item.Pricing?.Total;
	return price?.Converted ?? price?.Amount ?? item.Price ?? 0;
};
</script>

<style lang="scss" scoped>
.country-item {
	display: grid;
	grid-template-areas:
		'title price'
		'details price';

	&__container {
		grid-area: details;
	}

	&__user-info {
		display: flex;
		font-size: $font-small-2;

		span {
			width: max-content;
		}
	}

	&__name {
		line-height: 1.1;
		display: block;
		min-width: 0;
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		padding-right: 1rem;
		color: $color-text;
		font-size: $font-medium-3;
		font-weight: $font-weight-semibold;
		text-decoration: none;
		width: auto;
		padding: 0.1rem 0;
	}

	&__details {
		display: none;
		order: 2;
		width: 100%;
		margin-top: 2rem;
	}

	&__hotel,
	&__temperature-info,
	&__air,
	&__water,
	&__flight {
		display: flex;
		align-items: center;
	}

	&__flight {
		min-width: 6.4rem;

		&--hidden {
			min-width: 0;
			min-width: 6.4rem;
			opacity: 0;
		}
	}

	&__temperature-info {
		width: 13rem;
		justify-content: space-between;
		margin-right: 2.4rem;
	}

	&__hotel {
		font-style: italic;
		margin-right: 2.4rem;
		grid-area: title;
	}

	&__water {
		width: 5rem;
		margin-left: 2.4rem;
	}

	&__air,
	&__water,
	&__flight {
		&::before {
			content: '';
			display: inline-block;
			width: 1.8rem;
			height: 1.8rem;
			margin-right: 1rem;
			vertical-align: middle;
		}
	}

	&__air::before {
		background: url('~@assets/svg/sun-full.svg') center center no-repeat;
	}

	&__water::before {
		background: url('~@assets/svg/waves-uper.svg') center center no-repeat;
	}

	&__flight::before {
		background: url('~@assets/svg/plane-direct.svg') center center no-repeat;
	}

	&__price {
		text-align: right;
		align-content: center;
		grid-area: price;
	}

	&__button {
		display: inline-block;
		min-width: 10rem;
		padding: 0.25rem 1.5rem;
		border: none;
		border-radius: $border-radius-button;
		outline: none;
		color: $color-extra-light;
		font-size: $font-small-1;
		font-weight: $font-weight-semibold;
		text-decoration: none;
		display: flex;
		flex-direction: column;

		&:focus,
		&:hover,
		&:active {
			color: $color-extra-t8;
		}
	}

	&__button-prefix {
		font-size: $font-small-2;
		color: $color-black;
		font-weight: $font-weight-regular;
	}

	&.is-summary {
		min-height: 4.8rem;

		.country-item__details {
			display: none;
		}
	}

	@media (min-width: $breakpoint-small) {
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-size: $font-small-2;

		&__container {
			display: flex;
			width: 100%;
		}

		&__user-info {
			margin-left: 0;
			text-align: center;
		}

		&__name {
			@include text-ellipsis(100%);

			flex: initial;
			font-size: $font-medium-2;
		}

		&__price {
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			width: 100%;
			max-width: 14rem;
		}
	}

	@media (min-width: $breakpoint-medium) {
		&__details {
			display: flex;
			justify-content: flex-start;
			order: 0;
			width: auto;
			margin-top: unset;
		}

		&__user-info {
			margin-left: 5.1rem;
		}
	}

	&:focus-visible {
		outline: 0.2rem solid $color-primary;
		outline-offset: 0.2rem;
		box-shadow: 0 0 0 0.2rem rgba($color-primary, 0.2);
		z-index: 2;
	}
}
</style>
